<template>
  <div class="section container">
    <div class="section-header">
      <div class="section-title">
        <div class="section-icon section-icon-personal-information" :class="brandClass()"></div>
        <div class="section-title-label">
          {{ $t("profile_personal_data_information_summary_title") }}
        </div>
      </div>
      <div
        v-if="!editMode"
        class="section-edit pointer"
        :class="{ hidden: editDisabled }"
        @click="editHandler()"
        @keydown="ariaClick" role="button" tabindex="0"
      >
        <div class="section-edit-icon"></div>
        <div class="section-edit-label">
          {{ $t("profile_edit_button") }}
        </div>
      </div>
    </div>
    <form
      @submit.prevent="validateUser"
      v-if="editMode"
      novalidate
      class="form"
    >
      <div class="form_flex_container" id="personal-data-form-input" style="width:100%">
        <!-- SALUTATION -->
        <div class="custom-field" v-if="showSalutation" :style="{ order: getOrder('salutation') }">
          <OneSelect
            :brandClass="brandClass()"
            :t="$t"
            :profileStyle="true"
            :selectLabel="this.$t('generic_select_placeholder')"
            v-model="form.salutation"
            :form="v$.form"  
            :paramName="'salutation'"
            :disabled="salutationsOptions.length === 0"
            :options="salutationsOptions"
            :blank-option="salutationsBlankOpt"
            @blur="touchForm('salutation')"
            :label="$t('profile_salutation_label')+(isRequired('salutation') ? '*' : '')"
            :errors="[
              {message:$t('profile_salutation_error'), thereIsTheError:v$.form.salutation.required.$invalid}
            ]"
          ></OneSelect>
        </div>
        <!-- TITLE -->
        <div class="custom-field" v-if="showTitle" :style="{ order: getOrder('title') }">
          <OneSelect
            :brandClass="brandClass()"
            :t="$t"
            :profileStyle="true"
            :selectLabel="this.$t('generic_select_placeholder')"
            v-model="form.title"
            :form="v$.form"  
            :paramName="'title'"
            :disabled="getSelectOptions('list_of_titles').length === 0"
            :options="getSelectOptions('list_of_titles')"
            :blank-option="
              !isRequired('title')
                ? {
                    label: $t('title_blank'),
                    value: null,
                  }
                : null
            "
            @blur="touchForm('title')"
            :label="$t('profile_personal_data_edit_information_title')+(isRequired('title') ? '*' : '')"
            :errors="[
              {message:$t('profile_personal_data_edit_information_title_error'), thereIsTheError:v$.form.title.required.$invalid}
            ]"
          ></OneSelect>
        </div>

        <!-- FIRST NAME -->
        <div class="custom-field" 
          :style="{ order: getOrder('firstName') }">
          <OneInput
            :brandClass="brandClass()"
            :profileStyle="true"
            v-model.trim="form.firstName" 
            :form="v$.form"     
            :label="$t('profile_name_label')+(isRequired('firstName') ? '*' : '')" 
            :paramName="'firstName'"
            :id="'firstName'"
            @blur="touchForm('firstName')"
            :errors="[
              {message:$t('profile_name_error_required'), thereIsTheError:v$.form.firstName.required.$invalid},
              {message:$t('profile_error_min_length_prefix')+getMinLength('firstName')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.firstName.minLength.$invalid},
              {message:$t('profile_error_max_length_prefix')+getMaxLength('firstName')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.firstName.maxLength.$invalid},
              {message:$t('generic_error_char_not_allowed'), thereIsTheError:v$.form.firstName.customRegexpValidation.$invalid},
            ]"
          />
        </div>
        <!-- LAST NAME -->
        <div class="custom-field" 
          :style="{ order: getOrder('lastName') }">
          <OneInput
            :brandClass="brandClass()"
            :profileStyle="true"
            v-model.trim="form.lastName" 
            :form="v$.form"     
            :label="$t('profile_surname_label')+(isRequired('lastName') ? '*' : '')" 
            :paramName="'lastName'"
            :id="'lastName'"
            @blur="touchForm('lastName')"
            :errors="[
              {message:$t('profile_surname_error_required'), thereIsTheError:v$.form.lastName.required.$invalid},
              {message:$t('profile_error_min_length_prefix')+getMinLength('lastName')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.lastName.minLength.$invalid},
              {message:$t('profile_error_max_length_prefix')+getMaxLength('lastName')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.lastName.maxLength.$invalid},
              {message:$t('generic_error_char_not_allowed'), thereIsTheError:v$.form.lastName.customRegexpValidation.$invalid},
            ]"
          />
        </div>
        <!-- NICKNAME -->
        <div class="custom-field" 
          :style="{ order: getOrder('nickname') }">
          <OneInput
            :brandClass="brandClass()"
            :profileStyle="true"
            v-model.trim="form.nickname" 
            :form="v$.form"     
            :label="$t('profile_personal_data_information_summary_nickname')+(isRequired('nickname') ? '*' : '')" 
            :paramName="'nickname'"
            :id="'nickname'"
            @blur="touchForm('nickname')"
            :errors="[
              {message:$t('profile_nickname_error_required'), thereIsTheError:v$.form.nickname.required.$invalid},
              {message:$t('profile_error_min_length_prefix')+getMinLength('nickname')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.nickname.minLength.$invalid},
              {message:$t('profile_error_max_length_prefix')+getMaxLength('nickname')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.nickname.maxLength.$invalid},
              {message:$t('generic_error_char_not_allowed'), thereIsTheError:v$.form.nickname.customRegexpValidation.$invalid},
            ]"
          />
        </div>
        <!-- CUSTOM BIRTHDAY -->
        <div class="custom-field"
          :style="{ order: getOrder('birthday') }">
          <CustomBirthdayInput
            :profileStyle="true"
            v-model.trim="form.birthday"
            :form="v$.form"     
            :label="$t('profile_personal_data_information_summary_birthday')+(isRequired('birthday') ? '*' : '')" 
            :paramName="'birthday'"
            :id="'birthday'"
            @blur="touchForm('birthday')"
            :dateFormat="dateFormat"
            :errors="[
                {message:$t('profile_personal_data_edit_information_birthday_error_prefix')+' '+dateFormat+' '+$t('profile_personal_data_edit_information_birthday_error_suffix'), thereIsTheError:v$.form.birthday.validDate.$invalid},
                {message:$t('profile_personal_data_edit_information_birthday_error_past'), thereIsTheError:v$.form.birthday.pastDate.$invalid},
                {message:$t('profile_personal_data_edit_information_birthday_error_required'), thereIsTheError:v$.form.birthday.required.$invalid},
            ]"
          />
        </div>

        <!-- ADDITIONAL FAMILY NAME -->
        <div class="custom-field" 
          v-if="showAdditionalFamilyName"
          :style="{ order: getOrder('additionalFamilyName') }">
          <OneInput
            :brandClass="brandClass()"
            :profileStyle="true"
            v-model.trim="form.additionalFamilyName" 
            :form="v$.form"     
            :label="$t('profile_personal_data_edit_information_additional_family_name')+(isRequired('additionalFamilyName') ? '*' : '')" 
            :paramName="'additionalFamilyName'"
            :id="'additionalFamilyName'"
            @blur="touchForm('additionalFamilyName')"
            :errors="[
              {message:$t('profile_personal_data_edit_information_additional_family_name_error_required'), thereIsTheError:v$.form.additionalFamilyName.required.$invalid},
              {message:$t('profile_error_min_length_prefix')+getMinLength('additionalFamilyName')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.additionalFamilyName.minLength.$invalid},
              {message:$t('profile_error_max_length_prefix')+getMaxLength('additionalFamilyName')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.additionalFamilyName.maxLength.$invalid},
              {message:$t('generic_error_char_not_allowed'), thereIsTheError:v$.form.additionalFamilyName.customRegexpValidation.$invalid},
            ]"
          />
        </div>
        <!-- GENDER -->
        <div class="custom-field" v-if="showGender" :style="{ order: getOrder('gender') }">
          <OneSelect
            :brandClass="brandClass()"
            :t="$t"
            :profileStyle="true"
            :selectLabel="this.$t('generic_select_placeholder')"
            v-model="form.gender"
            :form="v$.form"  
            :paramName="'gender'"
            :disabled="gendersOptions.length === 0"
            :options="gendersOptions"
            @blur="touchForm('gender')"
            :label="$t('profile_personal_data_information_summary_gender')+(isRequired('gender') ? '*' : '')"
            :errors="[
                {message:$t('profile_personal_data_edit_information_gender_error_required'), thereIsTheError:v$.form.gender.required.$invalid}
              ]"
            ></OneSelect>
        </div>

        <!-- NATIONALITY -->
        <div class="custom-field" :style="{ order: getOrder('nationalityISO') }">
          <OneSelect
              :brandClass="brandClass()"
              :t="$t"
              :profileStyle="true"
              :selectLabel="this.$t('generic_select_placeholder')"
              v-model="form.nationalityISO"
              :form="v$.form"
              :paramName="'nationality'"
              :isSearchEnabled="true"
              :disabled=" nationalitiesOptions.length === 0"
              :options="nationalitiesOptions"
              :blank-option="
              !isRequired('nationality')
                ? {
                    label: $t('gender_blank'),
                    value: null,
                  }
                : null
            "
              @blur="touchForm('nationalityISO')"
              :label="$t('profile_personal_data_edit_information_nationality')+(isRequired('nationalityISO') ? '*' : '')"
              :errors="[]"
          />
        </div>

        <!-- BIRTH PLACE -->
        <div
          class="custom-field"
          :style="{ order: getOrder('birthPlace') }">
          <OneInput
            :brandClass="brandClass()"
            :profileStyle="true"
            v-model.trim="form.birthPlace" 
            :form="v$.form"     
            :label="$t('profile_personal_data_edit_information_birth_place') + (isRequired('birthPlace') ? '*' : '')" 
            :paramName="'birthPlace'"
            :id="'birthPlace'"
            @blur="touchForm('birthPlace')"
            :errors="[]"
          />
        </div>
        
        <!-- INITIALS NAME -->
        <div class="custom-field" 
          v-if="showInitials"
          :style="{ order: getOrder('initialsName') }">
          <OneInput
            :brandClass="brandClass()"
            :profileStyle="true"
            v-model.trim="form.initialsName" 
            :form="v$.form"     
            :label="$t('profile_personal_data_information_summary_initials_name')+(isRequired('initialsName') ? '*' : '')" 
            :paramName="'initialsName'"
            :id="'initialsName'"
            @blur="touchForm('initialsName')"
            :errors="[
              {message:$t('profile_personal_data_edit_information_initials_name_error_required'), thereIsTheError:v$.form.initialsName.required.$invalid},
              {message:$t('profile_error_min_length_prefix')+getMinLength('initialsName')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.initialsName.minLength.$invalid},
              {message:$t('profile_error_max_length_prefix')+getMaxLength('initialsName')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.initialsName.maxLength.$invalid},
              {message:$t('generic_error_char_not_allowed'), thereIsTheError:v$.form.initialsName.customRegexpValidation.$invalid},
            ]"
          />
        </div>
        <!-- ALTERNATE FIRST NAME -->

        <div class="custom-field" 
          v-if="showAlternateFirstName"
          :style="{ order: getOrder('alternateFirstName') }">
          <OneInput
            :brandClass="brandClass()"
            :profileStyle="true"
            v-model.trim="form.alternateFirstName" 
            :form="v$.form"     
            :label="$t('profile_personal_data_edit_information_alternative_first_name')+(isRequired('alternateFirstName') ? '*' : '')" 
            :paramName="'alternateFirstName'"
            :id="'alternateFirstName'"
            @blur="touchForm('alternateFirstName')"
            :errors="[
              {message:$t('profile_personal_data_edit_information_alternative_first_name_error_required'), thereIsTheError:v$.form.alternateFirstName.required.$invalid},
              {message:$t('profile_error_min_length_prefix')+getMinLength('alternateFirstName')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.alternateFirstName.minLength.$invalid},
              {message:$t('profile_error_max_length_prefix')+getMaxLength('alternateFirstName')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.alternateFirstName.maxLength.$invalid},
              {message:$t('generic_error_char_not_allowed'), thereIsTheError:v$.form.alternateFirstName.customRegexpValidation.$invalid},
            ]"
          />
        </div>
        <!-- ALTERNATE LAST NAME -->
        <div class="custom-field" 
          v-if="showAlternateLastName"
          :style="{ order: getOrder('alternateLastName') }">
          <OneInput
            :brandClass="brandClass()"
            :profileStyle="true"
            v-model.trim="form.alternateLastName" 
            :form="v$.form"     
            :label="$t('profile_personal_data_edit_information_last_name')+(isRequired('alternateLastName') ? '*' : '')" 
            :paramName="'alternateLastName'"
            :id="'alternateLastName'"
            @blur="touchForm('alternateLastName')"
            :errors="[
              {message:$t('profile_personal_data_edit_information_last_name_error_required'), thereIsTheError:v$.form.alternateLastName.required.$invalid},
              {message:$t('profile_error_min_length_prefix')+getMinLength('alternateLastName')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.alternateLastName.minLength.$invalid},
              {message:$t('profile_error_max_length_prefix')+getMaxLength('alternateLastName')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.alternateLastName.maxLength.$invalid},
              {message:$t('generic_error_char_not_allowed'), thereIsTheError:v$.form.alternateLastName.customRegexpValidation.$invalid},
            ]"
          />
        </div>
      </div>
      <div class="mandatory">*{{ $t("generic_mandatory_fields") }}</div>
      <div class="buttonsContainer">
        <OneButton
          :brandClass="brandClass()"
          :profileStyle="true"
          :type="'primary'"
          :label="$t('profile_apply_button')"
          :disabled="isButtonDisabled"
          @click="validateUser()"
        />
        <OneButton
          :brandClass="brandClass()"
          :profileStyle="true"
          v-if="editMode"
          :type="'secondary'"
          :label="$t('profile_cancel_button')"
          @click="cancelHandler()"
        />
      </div>
    </form>
    <div v-if="!editMode">
      <div class="infos personal-information">
        <div class="info details">
          <div class="label details-label">
            {{ $t("profile_personal_data_information_summary_personal_details") }}
          </div>
          <div class="value details-value">{{ personalDetailsText }}</div>
          <div v-if="personalDetailsSubText" class="value details-value">
            {{ personalDetailsSubText }}
          </div>
        </div>
        <div v-if="checkVisibility('birthday')" class="info birthday">
          <div class="label birthday-label">
            {{ $t("profile_personal_data_information_summary_birthday") }}
          </div>
          <div class="value birthday-value">
            {{ form.birthday }}
          </div>
        </div>
        <div
          v-if="(checkVisibility('initialsName'))"
          class="info with_margin"
        >
          <div class="label">
            {{ $t("profile_personal_data_information_summary_initials_name") }}
          </div>
          <div class="value">
            {{ getVauleWithPriority("initialsName") }}
          </div>
        </div>
        <div v-if="(checkVisibility('gender'))" class="info with_margin gender">
          <div class="label gender-label">{{ $t("profile_personal_data_information_summary_gender") }}</div>
          <div class="value gender-value">
            {{ getSelectLabelByValue("list_of_genders",form.gender) }}
          </div>
        </div>
        <div
          v-if="(checkVisibility('nickname'))"
          class="info with_margin nickname"
        >
          <div class="label nickname-label">
            {{ $t("profile_personal_data_information_summary_nickname") }}
          </div>
          <div class="value nickname-value">
            {{ getVauleWithPriority("nickname") }}
          </div>
        </div>
      </div>
    </div>
    <hr />
  </div>
</template>
<style lang="scss" scoped>
  .section-icon-personal-information {
    background-image: url("~@/assets/default/light/personal-info-default-light.svg");
    &.__d {
      background-image: url("~@/assets/default/dark/personal-info-default-dark.svg");
    }

    &.__alphabet {
      background-image: url("~@/assets/alphabet/light/personal-info-alphabet-light.svg");
    }
    &.__m {
      background-image: url("~@/assets/mini/light/personal-info-mini-light.svg");
      &.__d {
        background-image: url("~@/assets/mini/dark/personal-info-mini-dark.svg");
      }
    }

    &.__rr {
      background-image: url("~@/assets/default/dark/personal-info-default-dark.svg");
    }
    
  }
  .form_flex_container{
    margin-top: 22px;
  }
  .buttonsContainer{
    margin-top: 40px;
  }
  .d-flex {
    display: flex;
  }
  .edit-button-container {
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: end;
  }
  .mt-7px {
    margin-top: 7px;
  }
  .with-edit-button {
    width: calc(100% - 40px);
  }
</style>
<script>
// import oneMoment from "../../../../gcdm-one-library/gcdm-one-moment/src/bootstrap";
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";
import { requiredIf } from "@vuelidate/validators";
import useVuelidate from '@vuelidate/core';
import CustomBirthdayInput from "@/components/commons/CustomBirthdayInput";
import NotificationBox from "@/components/ProfileForm/components/NotificationBox";
import { defineComponent } from 'vue';

export default defineComponent({
  inject:['$c', '$t', 'brandClass'],
  setup() {
    return { v$: useVuelidate({$scope:'personalInformation'}) }
  },
  name: "ProfilePersonalInformation",
  emits: ['isEditMode'],
  components:{
    CustomBirthdayInput,
    NotificationBox
  },
  //validations from vuelidate
  mixins: [oneUtils.customValidationMixin, oneUtils.accessibilityMixin],
  props: {
    user: {},
    editDisabled: {
      default: false,
      type: Boolean,
    },
    salutationsFromServer: null,
    gendersFromServer: null,
    nationalitiesFromServer: null
  },
  data: () => ({
    form: {
      firstName: null,
      lastName: null,
      nickname: null,
      salutation: null,
      title: null,
      gender: null,
      birthday: null,
      additionalFamilyName: null,
      nationalityISO: null,
      // birthCountry: null,
      birthPlace: null,
      initialsName: null,
      alternateFirstName: null,
      alternateLastName: null,
    },
    isLoading: false,
    editMode: false,
  }),
  //vuelidate custom validations
  validations: {
    form: {
      firstName: {
        minLength: function (value) {
          return this.isMinLengthValid("firstName", value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid("firstName", value);
        },
        required: requiredIf(function (model) {
          return this.isRequired("firstName");
        }),
        customRegexpValidation: function (value) {
          return this.customRegexpValidation(value);
        },
      },
      lastName: {
        minLength: function (value) {
          return this.isMinLengthValid("lastName", value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid("lastName", value);
        },
        required: requiredIf(function (model) {
          return this.isRequired("lastName");
        }),
        customRegexpValidation: function (value) {
          return this.customRegexpValidation(value);
        },
      },
      nickname: {
        minLength: function (value) {
          return this.isMinLengthValid("nickname", value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid("nickname", value);
        },
        required: requiredIf(function (model) {
          return this.isRequired("nickname");
        }),
        customRegexpValidation: function (value) {
          return this.customRegexpValidation(value);
        },
      },
      birthday: {
        required: requiredIf(function (model) {
          return this.isRequired("birthday");
        }),
        validDate: function () {
          return (
            !this.form.birthday ||
            this.oneMoment.isValidDate(this.form.birthday, this.dateFormat)
          );
        },
        pastDate: function () {
          return (
            !this.form.birthday ||
            !this.v$.form.birthday.validDate ||
            this.oneMoment.isPastDate(this.form.birthday, this.dateFormat)
          );
        },
      },
      salutation: {
        required: requiredIf(function (model) {
          return this.isRequired("salutation");
        }),
      },
      title: {
        required: requiredIf(function (model) {
          return this.isRequired("title");
        }),
      },
      gender: {
        required: requiredIf(function (model) {
          return this.isRequired("gender");
        }),
      },
      additionalFamilyName: {
        minLength: function (value) {
          return this.isMinLengthValid("additionalFamilyName", value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid("additionalFamilyName", value);
        },
        required: requiredIf(function (model) {
          return this.isRequired("additionalFamilyName");
        }),
        customRegexpValidation: function (value) {
          return this.customRegexpValidation(value);
        },
      },
      nationalityISO: {},
      birthPlace: {
        required: requiredIf(function (model) {
          return this.isRequired("birthPlace");
        }),
      },
      initialsName: {
        minLength: function (value) {
          return this.isMinLengthValid("initialsName", value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid("initialsName", value);
        },
        required: requiredIf(function (model) {
          return this.isRequired("initialsName");
        }),
        customRegexpValidation: function (value) {
          return this.customRegexpValidation(value);
        },
      },
      alternateFirstName: {
        minLength: function (value) {
          return this.isMinLengthValid("alternateFirstName", value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid("alternateFirstName", value);
        },
        required: requiredIf(function (model) {
          return this.isRequired("alternateFirstName");
        }),
        customRegexpValidation: function (value) {
          return this.customRegexpValidation(value);
        },
      },
      alternateLastName: {
        minLength: function (value) {
          return this.isMinLengthValid("alternateLastName", value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid("alternateLastName", value);
        },
        required: requiredIf(function (model) {
          return this.isRequired("alternateLastName");
        }),
        customRegexpValidation: function (value) {
          return this.customRegexpValidation(value);
        },
      },
    },
  },
  watch: {
    "$store.getters.getUser": {
      deep: true,
      handler(newVal, oldVal) {
        this.prefillForm();
      },
    }
  },
  computed: {
    originalUser(){
      return this.$store.getters.getOriginalUser;
    },
    salutationsBlankOpt(){
      return !this.isRequired('salutation') && this.isRequired('salutation') !== undefined
              ? {
                  label: '',
                  value: null,
                }
              : null
    },
    currentCountry() {
      return this.$store.state.configurationParams.country;
    },
    isCurrentCountryItaly() {
      return this.currentCountry == "IT";
    },
    /**
     * Get and return salutaion, lastname, firstname, title and additionalFamilyName
     */
    personalDetailsText() {
      const opts = this.salutationsOptions;
      const opt = opts.find((o) => o.value === this.form.salutation);
      let salutation = opt
        ? (opt.label || this.$t(opt.translationKey))
        : '';

      if (this.user.salutation === "SAL_MX") {
        salutation = '';
      }
      let title = this.getSelectLabelByValue("list_of_titles", this.form.title);
      // const title = this.titleKeys[this.titleIndex] && this.titleValues.includes(this.form.title) ? this.form.title : "";
      const firstName = this.form.firstName || "";
      const lastName = this.form.lastName || "";
      const additionalFamilyName = this.form.additionalFamilyName || "";
      if (this.isHidden('salutation')){salutation = ''};
      if (this.isHidden('title')) { title = ''};
      if(this.currentCountry=="TW"){
        return `${salutation} ${lastName} ${firstName} ${title} ${additionalFamilyName}`.trim();
      }else if(this.currentCountry=="JP"){
        return `${title} ${lastName} ${firstName} ${salutation} ${additionalFamilyName}`.trim();
      }else{
        return `${salutation} ${title} ${firstName} ${lastName} ${additionalFamilyName}`.trim();
      }
    },
    /**
     * Get and return alternateLastName and alternateFistName
     */
    personalDetailsSubText() {
      const alternateFirstName = this.form.alternateFirstName || "";
      const alternateLastName = this.form.alternateLastName || "";
      if(this.currentCountry=="JP"){
        return `${alternateLastName} ${alternateFirstName}`.trim();
      }else{
        return `${alternateFirstName} ${alternateLastName}`.trim();
      }
    },
    dateFormat() {
      return this.sectionConfig && this.sectionConfig.birthday
        ? this.sectionConfig.birthday.dateFormat
        : "DD/MM/YYYY";
      // return 'M/D/YYYY'
    },
    showInitials() {
      return this.sectionConfig &&
        this.sectionConfig.initialsName &&
        this.sectionConfig.initialsName.priority === "ifNotNull"
        ? this.originalUser.initialsName
        : true;
    },
    showAlternateFirstName() {
      return this.sectionConfig &&
        this.sectionConfig.alternateFirstName &&
        this.sectionConfig.alternateFirstName.priority === "ifNotNull"
        ? this.originalUser.alternateFirstName
        : true;
    },
    showAlternateLastName() {
      return this.sectionConfig &&
        this.sectionConfig.alternateLastName &&
        this.sectionConfig.alternateLastName.priority === "ifNotNull"
        ? this.originalUser.alternateLastName
        : true;
    },
    showAdditionalFamilyName() {
      return this.sectionConfig &&
        this.sectionConfig.additionalFamilyName &&
        this.sectionConfig.additionalFamilyName.priority == "ifNotNull"
        ? this.originalUser.additionalFamilyName
        : true;
    },
    showSalutation() {
      if (this.sectionConfig &&
        this.sectionConfig.salutation &&
        this.sectionConfig.salutation.priority == "hidden"){
        return false;
      }
      return this.sectionConfig &&
        this.sectionConfig.salutation &&
        this.sectionConfig.salutation.priority == "ifNotNull"
        ? this.originalUser.salutation
        : true;
    },
    showGender() {
      if (this.sectionConfig &&
        this.sectionConfig.gender &&
        this.sectionConfig.gender.priority == "hidden"){
        return false;
      }
      return this.sectionConfig &&
        this.sectionConfig.gender &&
        this.sectionConfig.gender.priority == "ifNotNull"
        ? this.originalUser.gender
        : true;
    },
    showTitle() {
      if (this.sectionConfig &&
        this.sectionConfig.title &&
        this.sectionConfig.title.priority == "hidden"){
        return false;
      }
      return this.sectionConfig &&
        this.sectionConfig.title &&
        this.sectionConfig.title.priority == "ifNotNull"
        ? this.originalUser.title
        : true;
    },
    showBirthPlace() {
      return this.sectionConfig?.birthPlace?.priority == "ifNotNull" 
      ? this.originalUser.birthPlace
      : true;
    },
    sectionConfig() {
      return this.$c("_profileConfiguration").personalDataSection;
    },
    isButtonDisabled: function () {
      return this.v$.form.$invalid || this.isLoading;
    },
    salutationsOptions(){
      if(this.salutationsFromServer.length){
        return this.salutationsFromServer;
      }else{
        return this.getSelectOptions('list_of_salutations');
      }
    },
    gendersOptions(){
      if(this.gendersFromServer.length){
        return this.gendersFromServer;
      }else{
        return this.getSelectOptions('list_of_genders');
      }
    },
    nationalitiesOptions(){
      if(this.nationalitiesFromServer.length){
        return this.nationalitiesFromServer;
      }else{
        return this.getSelectOptions('list_of_countries');
      }
    },
  },
  async mounted() {
    //oneMoment import
    const awaitImport = await import('../../../../gcdm-one-library/gcdm-one-moment/src/bootstrap');
    this.oneMoment = (awaitImport).default;
    this.prefillForm();
  },
  updated() {
    this.$emit("isEditMode", { subSection: "PPI", value: this.editMode });
  },
  methods: {
    /**
     * This function check the visibility of a field
     */
    checkVisibility(key) {
      if(this.isHidden(key)){return false};
      if (
        this.isIfNotNull(key) ||
        this.isOptional(key)
      ) {
        return this.user[key];
      }
      return true;
    },
    /**
     * This function check the reccomended'key priority
     */
    getVauleWithPriority(key) {
      let fallbackLabel = "";
      if (this.isRecommended(key)) fallbackLabel = "-";
      return this.user[key] || fallbackLabel;
    },
    /**
     * This function populate the user form
    */
    prefillForm: function () {
      this.form.title = this.getSelectOptions("list_of_titles").some(
        (o) => o.value === this.user.title
      )
        ? this.user.title
        : null;
      this.touchForm("title");

      this.form.firstName = this.user.firstName;
      this.touchForm("firstName");

      this.form.lastName = this.user.lastName;
      this.touchForm("lastName");

      this.form.nickname = this.user.nickname;
      this.touchForm("nickname");

      this.form.initialsName = this.user.initialsName;
      this.touchForm("initialsName");

      this.form.alternateFirstName = this.user.alternateFirstName;
      this.touchForm("alternateFirstName");

      this.form.alternateLastName = this.user.alternateLastName;
      this.touchForm("alternateLastName");

      const opts = this.salutationsOptions;
      this.form.salutation = opts.some((o) => o.value === this.user.salutation)
        ? this.user.salutation
        : undefined;
      this.touchForm("salutation");

      const genderOpts = this.gendersOptions;
      this.form.gender = genderOpts.some((o) => o.value === this.user.gender)
        ? this.user.gender
        : null;
      this.touchForm("gender");

      this.form.birthday = this.user.birthday && this.oneMoment? (this.oneMoment.isValidDate(this.user.birthday, "YYYY-MM-DD")
        ? this.oneMoment.formatFromTo(this.user.birthday,"YYYY-MM-DD",this.dateFormat)
        : "") : undefined;
      this.touchForm("birthday");

      this.form.additionalFamilyName = this.user.additionalFamilyName;
      this.touchForm("additionalFamilyName");

      const nationalityOpts = this.nationalitiesOptions;
      this.form.nationalityISO = nationalityOpts.some((o) => o.value === this.user.nationalityISO)
        ? this.user.nationalityISO
        : null;
      this.touchForm("nationalityISO");

      this.form.birthPlace = this.user.birthPlace;
      this.touchForm("birthPlace");
    },
    /**
     * This function get and update the user information
    */
    validateUser() {
      this.submitUpdate();
      this.prefillForm();
      this.editMode = false;
    },
    /**
     * This function get the user information
    */
    submitUpdate() {
      this.handleSelectField('title');
      this.handleSelectField('gender');
      this.handleSelectField('nationalityISO');

      if(typeof this.form.firstName != 'undefined') this.user.firstName = this.form.firstName;
      if(typeof this.form.lastName != 'undefined') this.user.lastName = this.form.lastName;
      if(typeof this.form.nickname != 'undefined') this.user.nickname = this.form.nickname;
      if(typeof this.form.salutation != 'undefined') this.user.salutation = this.form.salutation;
      this.checkBirthday();
      if(typeof this.form.additionalFamilyName != 'undefined') this.user.additionalFamilyName = this.form.additionalFamilyName;
      if(typeof this.form.initialsName != 'undefined') this.user.initialsName = this.form.initialsName;
      if(typeof this.form.alternateFirstName != 'undefined') this.user.alternateFirstName = this.form.alternateFirstName;
      if(typeof this.form.alternateLastName != 'undefined') this.user.alternateLastName = this.form.alternateLastName;
      if(typeof this.form.birthPlace != 'undefined') this.user.birthPlace = this.form.birthPlace;
      if(typeof this.form.nationalityISO != 'undefined') this.user.nationalityISO = this.form.nationalityISO;

      // this.handleBirthCountryAndPlace();
    },
    handleSelectField(fieldName) {
      if(
        typeof this.form[fieldName] != 'undefined' && 
        this.form[fieldName] != this.user[fieldName]
      ) {
        if(!this.form[fieldName]) {
          this.user[fieldName] = "";
        }
        else {
          this.user[fieldName] = this.form[fieldName];
        }
      }
    },
    getBirthPlaceLabelFromValue(value) {
      let label = "";

      let values = value.split("-");
      values = values.slice(2);

      label += values.join("-");
      label = oneUtils.utils.capitalizeFirstLetter(label.toLowerCase());

      return label;
    },
    /**
     * This function get anche check the user birthday
    */
    checkBirthday(){
       if(typeof this.form.birthday != 'undefined') this.user.birthday = this.form.birthday
        ? this.oneMoment.formatFromTo(this.form.birthday,this.dateFormat,"YYYY-MM-DD")
        : "";
    },
    /**
     * This function reset the user form
    */
    cancelHandler() {
      // reset form
      this.prefillForm();
      // disable edit mode
      this.editMode = false;
    },
    /**
     * This function sort the element of the list
     */
    sortInDom(list,father){
      for (const elem of list) {
        elem.remove();
      }
      for (const elem of list) {
        father.appendChild(elem)
      }
    },
    /**
     * This function open the edit mode
     */
    editHandler() {
      // prefill form
      this.prefillForm();
      //enable edit mode
      this.editMode = true;
      
      setTimeout(() => {
        const el = document.querySelector('#personal-data-form-input');
        if(el && el.children){
          const list = Array.prototype.slice.call(el.children, 0);
          if(list[0] && list[0].parentNode){
            const father = list[0].parentNode;
            list.sort((a,b)=>{
              const first = Number(a.style?.order);
              const second = Number(b.style?.order);
              if (first<second) {
                return -1;
              }
              if (first>second) {
                return 1;
              }
              return 0;
            });
            this.sortInDom(list, father);
          }
        }
      }, 100);
    },
  },
});
</script>
